import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Paperclip, Send, X, FileText, Sparkles, ReplyAll, Reply, ChevronDown } from 'lucide-react';
import RichTextEditor from './RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Compositor de respuesta.
 * Props:
 *  - conv: objeto conversation
 *  - ultimoMsg: objeto message
 *  - accountFirmaHtml: firma HTML de la cuenta
 *  - onSent()
 *  - onCancel()
 */
export function ReplyComposer({ conv, ultimoMsg, accountFirmaHtml = '', onSent, onCancel, onTyping }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyAll, setReplyAll] = useState(false);
  const [error, setError] = useState('');
  const [cannedOpen, setCannedOpen] = useState(false);
  const [canned, setCanned] = useState([]);
  const [cannedFilter, setCannedFilter] = useState('');
  const fileInputRef = useRef(null);

  // Autosave del borrador: id devuelto por el backend + estado ("guardando" / "guardado")
  const [draftId, setDraftId] = useState(null);
  const [draftStatus, setDraftStatus] = useState(''); // 'guardando' | 'guardado' | 'error' | ''
  const [hydrating, setHydrating] = useState(true);   // durante la carga inicial del draft, bloqueamos edición
  const autosaveTimer = useRef(null);
  const draftLoadedRef = useRef(false);

  // Prefill defaults from conversation — con hidratación de borrador guardado.
  useEffect(() => {
    let cancelado = false;
    const init = async () => {
      draftLoadedRef.current = false;
      setHydrating(true);
      // 1) Buscar draft existente para este thread
      let draft = null;
      try {
        const r = await axios.get(`${API_URL}/bandeja-unificada/drafts/thread/${encodeURIComponent(conv.thread_id)}`);
        draft = r.data?.draft || null;
      } catch (_) { /* silent */ }
      if (cancelado) return;

      if (draft) {
        setDraftId(draft.id);
        setTo((draft.to || []).join(', '));
        setCc((draft.cc || []).join(', '));
        setShowCc(!!(draft.cc && draft.cc.length));
        setSubject(draft.subject || '');
        setBody(draft.body_html || '');
        setReplyAll(!!draft.reply_all);
        setDraftStatus('guardado');
      } else {
        // Defaults desde la conversación
        setDraftId(null);
        const fromMsg = (ultimoMsg?.from || conv.remitente || '').match(/<([^>]+)>/);
        const email = fromMsg ? fromMsg[1] : (ultimoMsg?.from || conv.remitente || '').trim();
        setTo(email);
        const s = conv.asunto || '';
        setSubject(s.toLowerCase().startsWith('re:') ? s : `Re: ${s}`);
        setBody('');
        if (replyAll && ultimoMsg) {
          const rawTo = (ultimoMsg.to || '').split(',').map(s => s.trim()).filter(Boolean);
          const rawCc = (ultimoMsg.cc || '').split(',').map(s => s.trim()).filter(Boolean);
          setCc([...rawTo, ...rawCc].join(', '));
          setShowCc(true);
        }
        setDraftStatus('');
      }
      // Liberamos edición (editor deja de estar bloqueado y el autosave puede correr)
      draftLoadedRef.current = true;
      setHydrating(false);
    };
    init();
    return () => { cancelado = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.thread_id, replyAll]);

  // Autosave debounced (~1200ms) cada vez que cambia to/cc/subject/body/attachments/replyAll.
  useEffect(() => {
    // No guardar durante la carga inicial ni mientras se está enviando.
    if (!draftLoadedRef.current || sending) return;
    // Sólo persistimos si hay body real (más que <p></p> vacío). El subject
    // "Re: ..." por sí solo NO genera un borrador — evita crear basura.
    const bodyLimpio = (body || '').replace(/<[^>]+>/g, '').trim();
    if (!bodyLimpio && attachments.length === 0) {
      return;
    }
    setDraftStatus('guardando');
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        const r = await axios.put(`${API_URL}/bandeja-unificada/drafts`, {
          thread_id: conv.thread_id,
          account_id: conv.account_id,
          to: parseEmails(to),
          cc: parseEmails(cc),
          subject,
          body_html: body,
          attachment_ids: attachments.map(a => a.id),
          reply_all: replyAll,
        });
        setDraftId(r.data?.draft?.id || null);
        setDraftStatus('guardado');
      } catch (_) {
        setDraftStatus('error');
      }
    }, 1200);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, cc, subject, body, attachments, replyAll]);

  const loadCanned = async () => {
    try {
      const r = await axios.get(`${API_URL}/bandeja-unificada/canned-responses`);
      setCanned(r.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadCanned(); }, []);

  const insertarCanned = (cr) => {
    setBody(b => (b || '') + cr.contenido_html);
    setCannedOpen(false);
  };

  const parseEmails = (raw) =>
    raw.split(/[,;]/).map(s => s.trim()).filter(s => s.includes('@'));

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const r = await axios.post(`${API_URL}/bandeja-unificada/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setAttachments(prev => [...prev, r.data]);
      } catch (err) {
        setError(err.response?.data?.detail || `Error subiendo ${f.name}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const quitarAdjunto = (id) => setAttachments(a => a.filter(x => x.id !== id));

  const enviar = async () => {
    setError('');
    const toList = parseEmails(to);
    if (toList.length === 0) { setError('Ingresá al menos un destinatario válido'); return; }
    const ccList = parseEmails(cc);
    const bodyLimpio = (body || '').trim();
    if (!bodyLimpio || bodyLimpio === '<p></p>') { setError('Escribí un mensaje antes de enviar'); return; }

    setSending(true);
    try {
      await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/reply`, {
        to: toList,
        cc: ccList,
        subject: subject.trim() || undefined,
        body_html: body,
        body_text: '',
        attachment_ids: attachments.map(a => a.id),
        incluir_firma: true,
        reply_all: replyAll,
      });
      // Limpiar borrador tras envío exitoso — silent si falla.
      if (draftId) {
        try { await axios.delete(`${API_URL}/bandeja-unificada/drafts/${draftId}`); } catch (_) {}
      }
      onSent?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al enviar la respuesta');
    }
    setSending(false);
  };

  const cannedFiltradas = canned.filter(c =>
    !cannedFilter ||
    c.titulo.toLowerCase().includes(cannedFilter.toLowerCase()) ||
    (c.categoria || '').toLowerCase().includes(cannedFilter.toLowerCase())
  );

  return (
    <div
      className="border-t bg-slate-50 flex-shrink-0 flex flex-col max-h-[70vh] md:max-h-[65vh] overflow-hidden"
      data-testid="reply-composer"
    >
      {/* Header sticky: Reply / Reply-all + BARRA DE ACCIONES (Enviar SIEMPRE visible) */}
      <div className="flex items-center gap-2 px-3 pt-2 pb-2 border-b bg-slate-50 flex-shrink-0 flex-wrap">
        <button
          onClick={() => setReplyAll(false)}
          className={`px-2 py-1 rounded text-xs ${!replyAll ? 'bg-purple-100 text-purple-800 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid="btn-reply"
        >
          <Reply className="w-3 h-3 inline mr-1" /> Responder
        </button>
        <button
          onClick={() => setReplyAll(true)}
          className={`px-2 py-1 rounded text-xs ${replyAll ? 'bg-purple-100 text-purple-800 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid="btn-reply-all"
        >
          <ReplyAll className="w-3 h-3 inline mr-1" /> Responder a todos
        </button>

        {/* Indicador de autosave */}
        {draftStatus && (
          <span
            className={`text-[11px] italic flex items-center gap-1 ${
              draftStatus === 'guardado' ? 'text-emerald-600'
              : draftStatus === 'error' ? 'text-red-500'
              : 'text-gray-500'
            }`}
            data-testid="draft-status"
          >
            {draftStatus === 'guardando' && <Loader2 className="w-3 h-3 animate-spin" />}
            {draftStatus === 'guardando' ? 'Guardando borrador…'
              : draftStatus === 'guardado' ? '✓ Borrador guardado'
              : '⚠ No se pudo guardar'}
          </span>
        )}

        <div className="flex-1" />

        {/* Acciones agrupadas — el botón Enviar SIEMPRE visible arriba */}
        <input ref={fileInputRef} type="file" multiple hidden onChange={onFilePick} data-testid="file-input" />
        <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="btn-adjuntar">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          <span className="ml-1 text-xs hidden sm:inline">Adjuntar</span>
        </Button>

        <div className="relative">
          <Button size="sm" variant="outline" onClick={() => setCannedOpen(o => !o)} data-testid="btn-canned-open">
            <Sparkles className="w-4 h-4" />
            <span className="ml-1 text-xs hidden sm:inline">Frecuente</span>
            <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
          {cannedOpen && (
            <div className="absolute z-40 top-full mt-1 right-0 bg-white border rounded-lg shadow-lg w-80 max-h-72 overflow-hidden flex flex-col" data-testid="canned-dropdown">
              <div className="p-2 border-b">
                <Input placeholder="Buscar…" value={cannedFilter} onChange={e => setCannedFilter(e.target.value)} className="h-7 text-xs" data-testid="canned-filter" />
              </div>
              <div className="overflow-y-auto flex-1">
                {cannedFiltradas.length === 0 ? (
                  <p className="p-3 text-xs text-gray-500 italic">
                    {canned.length === 0 ? 'Aún no creaste respuestas frecuentes. Andá a Configuración → Bandeja.' : 'Sin resultados'}
                  </p>
                ) : cannedFiltradas.map(c => (
                  <button
                    key={c.id}
                    onClick={() => insertarCanned(c)}
                    className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b last:border-b-0"
                    data-testid={`canned-item-${c.id}`}
                  >
                    <p className="font-medium text-sm truncate">{c.titulo}</p>
                    {c.categoria && <p className="text-xs text-gray-500">{c.categoria}</p>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="btn-cancel-reply">Cancelar</Button>
        <Button
          size="sm"
          onClick={enviar}
          disabled={sending}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="btn-send-reply"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          <span className="ml-1">Enviar</span>
        </Button>
      </div>

      {/* Body scrolleable independiente: To/Cc/Subject + editor + adjuntos.
          Con `overflow-y-auto` propio, el editor NO empuja los botones fuera
          de pantalla — la barra de arriba y el botón Enviar siempre son visibles. */}
      <div className="overflow-y-auto flex-1 min-h-0 p-3 space-y-2" data-testid="reply-composer-body">

      <div className="grid grid-cols-1 sm:grid-cols-[60px_1fr] gap-x-2 gap-y-2 sm:items-center text-sm">
        <Label className="text-xs text-gray-600 sm:pt-1">Para:</Label>
        <Input value={to} onChange={e => setTo(e.target.value)} className="h-8" data-testid="input-reply-to" onKeyDown={onTyping} />
        {showCc ? (
          <>
            <Label className="text-xs text-gray-600">Cc:</Label>
            <Input value={cc} onChange={e => setCc(e.target.value)} className="h-8" data-testid="input-reply-cc" onKeyDown={onTyping} />
          </>
        ) : (
          <div className="col-span-2 -mt-1">
            <button className="text-xs text-purple-700 hover:underline" onClick={() => setShowCc(true)} data-testid="btn-show-cc">+ Añadir Cc</button>
          </div>
        )}
        <Label className="text-xs text-gray-600">Asunto:</Label>
        <Input value={subject} onChange={e => setSubject(e.target.value)} className="h-8" data-testid="input-reply-subject" onKeyDown={onTyping} />
      </div>

      <div onKeyDown={onTyping} className="relative">
        {hydrating && (
          <div
            className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[1px] flex items-center justify-center pointer-events-auto"
            data-testid="composer-hydrating"
          >
            <div className="flex items-center gap-2 text-xs text-gray-500 bg-white px-3 py-1.5 rounded-full shadow-sm border">
              <Loader2 className="w-3 h-3 animate-spin" /> Cargando borrador…
            </div>
          </div>
        )}
        <RichTextEditor value={body} onChange={setBody} autoFocus editable={!hydrating} minHeight={120} />
      </div>

      {/* Preview firma */}
      {accountFirmaHtml && (
        <details className="text-xs text-gray-600 border rounded bg-white p-2">
          <summary className="cursor-pointer">Firma que se adjuntará automáticamente</summary>
          <div className="mt-2 prose prose-sm max-w-none border-t pt-2" dangerouslySetInnerHTML={{ __html: accountFirmaHtml }} />
        </details>
      )}

      {/* Adjuntos */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="lista-adjuntos">
          {attachments.map(a => (
            <div key={a.id} className="inline-flex items-center gap-1.5 bg-white border rounded px-2 py-1 text-xs">
              <FileText className="w-3.5 h-3.5 text-purple-600" />
              <span className="max-w-[180px] truncate">{a.filename}</span>
              <span className="text-gray-500">{Math.round(a.size / 1024)} KB</span>
              <button onClick={() => quitarAdjunto(a.id)} className="text-red-500 hover:text-red-700" data-testid={`btn-quitar-adjunto-${a.id}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-600" data-testid="reply-error">{error}</p>}

      </div>{/* /body scrolleable */}
    </div>
  );
}

export default ReplyComposer;
